import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button, EmptyState, Spinner, Icon } from "@cntrl/ui";
import { useCapability } from "../../../app/bandContext.js";
import { useRelease, useUpdateRelease, useDeleteMilestone, releaseUpdateBody } from "../../../app/hooks/useRelease.js";
import type { components } from "@cntrl/api-client";
import { SectionRail } from "../sectionRail.js";
import { MilestoneModal } from "../MilestoneModal.js";
import { formatShortDate } from "../releaseFormat.js";
import styles from "../section.module.scss";
import own from "./TimelineSection.module.scss";

type MilestoneDto = components["schemas"]["MilestoneDto"];

// Timeline (shot-15) — the release-date card (push/change) + vertical milestone timeline with add/edit/
// remove. Milestones are per-row CRUD (no If-Match); the release date is a full PUT (If-Match). "Edit dates"
// reveals the remove controls. Timeline drives the day-in-cycle math everywhere it renders.

export function TimelineSection({ releaseId }: { releaseId: string }) {
  const canWrite = useCapability("releases:write");
  const q = useRelease(releaseId);
  const update = useUpdateRelease(releaseId);
  const remove = useDeleteMilestone(releaseId);
  const [editMode, setEditMode] = useState(false);
  const [modal, setModal] = useState<{ milestone?: MilestoneDto } | null>(null);

  const overview = q.data?.data?.overview;
  const version = q.data?.version;
  const milestones = [...(q.data?.data?.milestones ?? [])].sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));

  if (q.isLoading || !overview) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading timeline…" />
      </div>
    );
  }

  function changeDate(date: string) {
    if (version === undefined) return;
    update.mutate({
      version,
      body: releaseUpdateBody(overview!, { releaseDate: date || null }),
    });
  }

  return (
    <div className={styles.section}>
      <SectionRail>
        {canWrite ? (
          <>
            <Button variant="ghost" size="sm" onClick={() => setEditMode((v) => !v)}>
              {editMode ? "Done" : "Edit dates"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setModal({})}>
              <Icon icon={Plus} size={16} /> Add milestone
            </Button>
          </>
        ) : null}
      </SectionRail>

      <div className={own.dateCard}>
        <div>
          <span className={styles.eyebrow}>Release date</span>
          <p className={own.dateBig}>{formatShortDate(overview.releaseDate) || "Not set"}</p>
        </div>
        {canWrite && (
          <label className={own.dateInputLabel}>
            Push / change
            <input
              className={own.dateInput}
              type="date"
              value={overview.releaseDate ?? ""}
              disabled={update.isPending}
              onChange={(e) => changeDate(e.target.value)}
            />
          </label>
        )}
      </div>

      {milestones.length === 0 ? (
        <EmptyState
          title="No milestones yet"
          description="Add key dates — announce, pre-save, drop day — to build the rollout timeline."
          action={canWrite ? <Button onClick={() => setModal({})}>Add milestone</Button> : undefined}
        />
      ) : (
        <ol className={own.timeline}>
          {milestones.map((m) => (
            <li key={m.milestoneId} className={own.milestone}>
              <span className={own.dot} aria-hidden="true" />
              <div className={own.milestoneMain}>
                <span className={own.milestoneDate}>{formatShortDate(m.date)}</span>
                <span className={own.milestoneName}>{m.name || "Milestone"}</span>
                {m.description && <span className={own.milestoneDesc}>{m.description}</span>}
              </div>
              {canWrite && editMode && (
                <div className={own.milestoneActions}>
                  <button type="button" className={own.iconBtn} aria-label="Edit milestone" onClick={() => setModal({ milestone: m })}>
                    <Icon icon={Pencil} size={16} />
                  </button>
                  <button
                    type="button"
                    className={own.iconBtn}
                    aria-label="Remove milestone"
                    onClick={() => remove.mutate(m.milestoneId as string)}
                  >
                    <Icon icon={Trash2} size={16} />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {modal && <MilestoneModal releaseId={releaseId} milestone={modal.milestone} onClose={() => setModal(null)} />}
    </div>
  );
}
